// LoginPage.js
import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import LOGINIMAGE from '../../assets/loginimage.jpg';

const GlobalStyle = createGlobalStyle`
  @font-face {
    font-family: 'Very Sans Light';
    src: url('/assets/fonts/VerySans-LightText.ttf') format('truetype');
    font-weight: 300;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Very Sans Medium';
    src: url('/assets/fonts/VerySans-MediumText.ttf') format('truetype');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Very Sans Bold';
    src: url('/assets/fonts/VerySans-BoldText.ttf') format('truetype');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Very Sans Headline';
    src: url('/assets/fonts/VerySans-Headline.ttf') format('truetype');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Very Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    line-height: 1.6;
    color: #ffffff;
    background: linear-gradient(135deg, #020611 0%, #050B15 50%, #020611 100%);
    overflow-x: hidden;
  }
`;

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  background: linear-gradient(135deg, #020611 0%, #050B15 50%, #020611 100%);
  position: relative;
`;

const LeftPanel = styled.div`
  flex: 1;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  background: transparent;
  
  @media (max-width: 768px) {
    max-width: 100%;
    padding: 1.5rem;
  }
`;

const RightPanel = styled.div`
  flex: 1;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  background-image: url(${LOGINIMAGE});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  
  /* Dark overlay */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(2, 6, 17, 0.7) 0%, rgba(5, 11, 21, 0.8) 50%, rgba(2, 6, 17, 0.7) 100%);
    z-index: 1;
  }
  
  /* Blue accent overlay */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at center, rgba(59, 130, 246, 0.2) 0%, transparent 70%);
    z-index: 2;
  }
`;

const Logo = styled.div`
  margin-bottom: 2rem;
  text-align: center;
  
  h1 {
    font-size: 2rem;
    font-weight: 600;
    color: #ffffff;
    font-family: Very Sans Bold, Sans-serif;
    margin-bottom: 0.5rem;
  }
`;

const LoginForm = styled.form`
  width: 100%;
  max-width: 360px;
  display: flex;
  flex-direction: column;
`;

const Title = styled.h2`
  text-align: left;
  margin-bottom: 1.5rem;
  color: #ffffff;
  font-family: Very Sans Bold, sans-serif;
  font-size: 1.75rem;
  font-weight: 600;
`;

const SubTitle = styled.p`
  text-align: left;
  margin-bottom: 2rem;
  color: #94A3B8;
  font-family: Very Sans Medium, sans-serif;
  font-size: 0.95rem;
  
  a {
    color: #3B82F6;
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #94A3B8;
  font-family: Very Sans Medium, sans-serif;
  font-size: 0.9rem;
  font-weight: 400;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 0.95rem;
  font-family: Very Sans Medium, sans-serif;
  color: #ffffff;
  transition: all 0.2s ease;
  
  &::placeholder {
    color: #94A3B8;
  }
  
  &:focus {
    outline: none;
    border-color: #3B82F6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    background: rgba(255, 255, 255, 0.08);
  }
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  color: #94A3B8;
  font-family: Very Sans Medium, sans-serif;
  font-size: 0.9rem;
  cursor: pointer;
  
  input {
    margin-right: 0.75rem;
    transform: scale(1.1);
    accent-color: #3B82F6;
    cursor: pointer;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 0.875rem 1rem;
  background: #3B82F6;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  font-family: Very Sans Bold, sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 1rem;
  
  &:hover {
    background: #2563EB;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
  
  &:disabled {
    background: rgba(255, 255, 255, 0.1);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 6px;
  text-align: center;
  font-family: Very Sans Medium, sans-serif;
  font-size: 0.9rem;
`;

const SuccessMessage = styled.div`
  color: #10b981;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 6px;
  text-align: center;
  font-family: Very Sans Medium, sans-serif;
  font-size: 0.9rem;
`;

const LinkText = styled.p`
  text-align: center;
  margin-top: 1rem;
  font-family: Very Sans Light, sans-serif;
  color: #94A3B8;
  font-size: 0.9rem;
  
  a {
    color: #3B82F6;
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Footer = styled.div`
  margin-top: 2rem;
  text-align: center;
  color: #94A3B8;
  font-size: 0.8rem;
  font-family: Very Sans Light, sans-serif;
  
  a {
    color: #3B82F6;
    text-decoration: none;
    margin: 0 0.5rem;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const GradientOrb1 = styled.div`
    position: absolute;
    top: -20%;
    left: -10%;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%);
    pointer-events: none;
    overflow: hidden;
    
    @media screen and (max-width: 768px) {
        width: 400px;
        height: 400px;
    }
`;

const GradientOrb2 = styled.div`
    position: absolute;
    bottom: 20%;
    right: -10%;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%);
    pointer-events: none;
    
    @media screen and (max-width: 768px) {
        width: 400px;
        height: 400px;
    }
`;

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(email, password);
    
    if (result.success) {
      navigate('/autoPullpage');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <>
      <GlobalStyle />
      <LoginContainer>
        <GradientOrb1 />
        <GradientOrb2 />
        <LeftPanel>
          <Logo>
            <h1>Hydrixpay</h1>
          </Logo>
          
          <LoginForm onSubmit={handleSubmit}>
            <Title>Login</Title>
            <SubTitle>
              Don't have an account? <Link to="/signup">Sign Up Now</Link>
            </SubTitle>
            
            {error && <ErrorMessage>{error}</ErrorMessage>}
            
            <FormGroup>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </FormGroup>
            
            <CheckboxContainer>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                Remember Me
              </CheckboxLabel>
            </CheckboxContainer>
            
            <Button type="submit" disabled={loading}>
              {loading ? 'Logging in...' : 'Log In'}
            </Button>
            
            <LinkText>
              <Link to="/forgot-password">Forgot Your Password?</Link>
            </LinkText>
          </LoginForm>
          
          <Footer>
            Copyright © 2025 Hydrix LTD. Hydrix™ is a trademark of Hydrix LTD
            <br />
            <a href="/terms">Terms of Service</a> | <a href="/privacy">Privacy Policy</a>
          </Footer>
        </LeftPanel>
        
        <RightPanel>
          <ImagePlaceholder />
        </RightPanel>
      </LoginContainer>
    </>
  );
}